import { useEffect, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { TableHead } from '@/components/ui/table'
import { useDebounced } from '@/hooks/use-debounced'
import { formatNumber } from '@/lib/format'

/** A search box that writes to the URL a moment after the typing stops. */
export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  const [text, setText] = useState(value)
  const debounced = useDebounced(text, 300)

  useEffect(() => setText(value), [value])
  useEffect(() => {
    if (debounced !== value) onChange(debounced)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  return (
    <div className="relative w-full sm:w-72">
      <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="bg-card pr-8 pl-8"
      />
      {text && (
        <button
          type="button"
          onClick={() => setText('')}
          className="absolute top-1/2 right-2 -translate-y-1/2 rounded text-muted-foreground hover:text-foreground"
          aria-label="Clear search"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  )
}

export interface Option {
  value: string
  label: string
}

/** A dropdown filter. The empty value means "no filter" and shows the label itself. */
export function FilterSelect({ label, value, options, onChange }: { label: string; value: string; options: Option[]; onChange: (v: string) => void }) {
  const ALL = '__all'
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? '' : v)}>
      <SelectTrigger size="sm" aria-label={label} className={cn('bg-card', value && 'border-primary/50 text-accent-foreground')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{label}: any</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** An on/off filter chip. */
export function ToggleChip({ pressed, onChange, children }: { pressed: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={() => onChange(!pressed)}
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[0.8rem] font-medium',
        pressed ? 'border-primary/50 bg-accent text-accent-foreground' : 'border-input bg-card text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

/** A table header that sorts the list through the URL. */
export function SortHead({
  id,
  sort,
  order,
  onSort,
  children,
  align = 'left',
  className,
}: {
  id: string
  sort: string
  order: string
  onSort: (id: string, order: string) => void
  children: ReactNode
  align?: 'left' | 'right'
  className?: string
}) {
  const active = sort === id
  const Icon = order === 'asc' ? ArrowUp : ArrowDown
  return (
    <TableHead className={cn(align === 'right' && 'text-right', className)} aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(id, active && order === 'desc' ? 'asc' : 'desc')}
        className={cn('inline-flex items-center gap-1 hover:text-foreground', active && 'text-foreground', align === 'right' && 'flex-row-reverse')}
      >
        {children}
        <Icon className={cn('size-3', active ? 'opacity-100' : 'opacity-0')} aria-hidden />
      </button>
    </TableHead>
  )
}

export const PAGE_SIZE = 50

export function Pagination({ total, offset, pageSize = PAGE_SIZE, onChange }: { total: number; offset: number; pageSize?: number; onChange: (offset: number) => void }) {
  if (total === 0) return null
  const from = offset + 1
  const to = Math.min(offset + pageSize, total)
  return (
    <div className="flex items-center justify-between border-t px-4 py-2.5 text-muted-foreground">
      <span className="figures">
        {formatNumber(from)}–{formatNumber(to)} of {formatNumber(total)}
      </span>
      <div className="flex gap-1">
        <Button variant="outline" size="icon-sm" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - pageSize))} aria-label="Previous page">
          <ChevronLeft />
        </Button>
        <Button variant="outline" size="icon-sm" disabled={to >= total} onClick={() => onChange(offset + pageSize)} aria-label="Next page">
          <ChevronRight />
        </Button>
      </div>
    </div>
  )
}
